import dayjs from 'dayjs';
import { useCallback } from 'react';

import { LocationActions } from '@Actions/index';
import { NotificationType, showNotification } from '@Components/design-system/notification/notification';
import { IQueryData, updateLocationCode } from '@Reducers/common';
import { updateSortedClinicLocations } from '@Reducers/location';
import { IClinicLocationsData } from '@Reducers/schedule';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { CONSULTATION_TYPE } from '@Utils/constants';
import { sortClinicsByDistance } from '@Utils/Helpers/location/helper';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';
import usePosthog, { CtaEvents } from '@Utils/hooks/posthog';

import useBrowserLocation from '../../browser-location';
import useConsultationType from '../../consultation-type';
import { Direction } from '../constants';
import useOnEnter from './on-enter';
import { Rule } from './type';

export const useClinicRule = (): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const { consultationType } = useConsultationType();
  const userPosition = useBrowserLocation(true);
  const queryData: IQueryData = useAppSelector((state) => state.common?.queryData);
  const locationIsLoading: boolean = useAppSelector((state) => state.location.isLoading);
  const clinicLocations: Record<string, IClinicLocationsData[]> = useAppSelector(
    (state) => state.location?.offlineClinicLocations,
  );
  const { ctaEventCapture } = usePosthog();

  const next = useCallback(() => {
    ctaEventCapture(CtaEvents.CLINIC);
    if (!queryData?.locationCode) {
      showNotification(NotificationType.ERROR, 'Please select a clinic', 3000);
      return;
    }
    if (
      queryData?.utmSource === 'fb' &&
      queryData?.consultationCode === CONSULTATION_TYPE.SC &&
      !queryData?.skipQuestionnaire
    ) {
      router.push({ route: Routes.APPOINTMENT, subRoute: Direction.QUESTIONNAIRE });
      return;
    }
    router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
  }, [router, queryData?.locationCode]);

  useOnEnter(() => {
    if (!consultationType?.consultationTypeId) return;
    dispatch(
      LocationActions.GetProviderLocations({
        typeId: consultationType.consultationTypeId,
        startTime: dayjs()?.startOf('day')?.toISOString(),
        endTime: dayjs().add(7, 'day').endOf('day')?.toISOString(),
      }),
    );

    //preselect the location
    const sortedClinics = userPosition ? sortClinicsByDistance(clinicLocations, userPosition) : clinicLocations;
    if (userPosition) dispatch(updateSortedClinicLocations(sortedClinics));

    // only update the location code if the location code is out of city clinics
    if (sortedClinics?.[queryData?.validUserCity]?.every((clinic) => clinic.code !== queryData?.locationCode)) {
      dispatch(updateLocationCode(sortedClinics?.[queryData?.validUserCity]?.[0].code));
    }
  }, [consultationType?.consultationTypeId]);

  return {
    next: next,
    disableNext: !queryData?.locationCode,
    isLoading: locationIsLoading && Object.keys(clinicLocations || {}).length === 0,
  };
};
